import clsx from 'clsx';
import slugify from 'slugify';

import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AngularUiFormConfig } from '../angular-ui-form.config';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { isPlatformBrowser, NgClass } from '@angular/common';
import { DxTextBoxComponent, DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { ValidationRuleType } from 'devextreme/common';
import { DxDateBoxModule } from 'devextreme-angular/ui/date-box';
import { DxCheckBoxModule } from 'devextreme-angular/ui/check-box';
import { DxNumberBoxModule } from 'devextreme-angular/ui/number-box';
import { DxTextAreaModule } from 'devextreme-angular/ui/text-area';
import { DxiValidationRuleModule } from 'devextreme-angular/ui/nested';
import { DxValidatorModule } from 'devextreme-angular/ui/validator';

export type TextFieldValidationRule = {
  type: ValidationRuleType;
  message: string;
  min?: number | Date;
  max?: number | Date;
  pattern?: RegExp;
  comparisonTarget?: () => string;
  validationCallback?: () => boolean;
};
export type TextFieldLabelSize = 'xs' | 'sm' | 'md';
export type TextFieldType =
  | 'text'
  | 'password'
  | 'textarea'
  | 'number'
  | 'currency'
  | 'date'
  | 'checkbox';

export type TextFieldMode = 'email' | 'password' | 'search' | 'tel' | 'text' | 'url';

type InputAttr = {
  [key: string]: string;
  id: string;
  pattern: string | any;
};

let INPUT_UID = 0;

@Component({
    selector: 'pxw-text-field',
    templateUrl: './text-field.component.html',
    styleUrls: ['./text-field.component.scss'],
    imports: [
        NgClass,
        DxTextBoxModule,
        DxValidatorModule,
        DxiValidationRuleModule,
        DxTextAreaModule,
        DxNumberBoxModule,
        DxCheckBoxModule,
        DxDateBoxModule,
    ]
})
export class TextFieldComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('input') input: DxTextBoxComponent;

  @Input() rules: TextFieldValidationRule[] | undefined;
  @Input() label = '';
  @Input() labelSize: TextFieldLabelSize = 'sm';
  @Input() placeholder = '';
  @Input() type: TextFieldType = 'text';
  @Input() mode: TextFieldMode = 'text';
  @Input() value: any;
  @Input() disabled = false;
  @Input() readOnly = false;
  @Input() min: number | undefined;
  @Input() max: number | undefined;
  @Input() helper?: string;
  @Input() inputAttr: Record<string, string> = {};
  @Input() mask = null;
  @Input() maskInvalidMessage: string | undefined;
  @Input() maxLength = null;
  @Input() isValid = true;
  @Input() validationError: { message: string } | undefined = undefined;
  @Input() required = false;
  @Input() showClearButton = false;
  @Input() stylingMode: 'outlined' | 'underlined' | 'filled' = 'outlined';
  @Input() tabIndex = 0;
  @Input() autofocus = false;
  @Input() debounceTime = 350;
  @Input() valueChangeEvent = 'change';
  @Input() currencySymbol?: string;

  // This probably should be moved to a dedicated component, e.g.: pxw-validation
  @Input() validationOnly = false;

  @Output() valueChange = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() focusIn = new EventEmitter();
  @Output() focusOut = new EventEmitter();
  @Output() keyPress = new EventEmitter();
  @Output() keyDown = new EventEmitter();
  @Output() keyUp = new EventEmitter();

  @HostBinding('class') get classNames() {
    return clsx('text-field', `ui-input-${this.type}`, {
      'ui-input': !this.validationOnly,
      'ui-input--validation-only': this.validationOnly,
      'ui-input--with-helper': this.helper,
      'ui-input--with-errors': !this.isValid,
    });
  }

  inputId: string;
  inputAttrs: InputAttr;
  keyUp$ = new Subject<KeyboardEvent>();

  private platformId = inject(PLATFORM_ID);

  public rootElement = inject(ElementRef);
  private textFieldConfig = inject(AngularUiFormConfig);
  private destroyRef = inject(DestroyRef);

  constructor() {
    this.inputAttrs = Object.assign({ id: this.inputId, pattern: undefined }, this.inputAttr);
    this.inputId = this.getInputId(++INPUT_UID, this.inputAttrs);
  }

  ngOnInit() {
    if (!this.maskInvalidMessage) {
      this.maskInvalidMessage = this.textFieldConfig.getMaskInvalidFormatText();
    }

    if (this.required) {
      const requiredRule = {
        type: 'required' as ValidationRuleType,
        message: this.textFieldConfig.getValidationRequiredText(),
      };

      if (this.rules) {
        this.rules.unshift(requiredRule);
      } else {
        this.rules = [requiredRule];
      }
    }

    if (!this.mask) {
      // By default, "onValueChanged" method is only called on blur or after pressing Enter key (bound to "change" event)
      // This means that the user can type a lot of text, but as long as it doesn't "commit" their changes they are not in sync with the model
      // If we used "keyup" event instead, it would trigger ChangeDetection more often than required
      // By creating this debounced pipe, we ensure that model keeps in sync without triggering ChangeDetection very often
      this.keyUp$.pipe(debounceTime(this.debounceTime), takeUntilDestroyed(this.destroyRef)).subscribe(event => {
        this.onValueChanged((event.target as HTMLInputElement)?.value);
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.autofocus && this.input) {
      this.input.instance.focus();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['inputAttr']?.currentValue !== changes['inputAttr']?.previousValue) {
      this.inputAttrs = Object.assign(
        { id: this.inputId, pattern: undefined },
        this.inputAttr || {},
      );
    }

    if (changes['required']) {
      this.updateRequiredValidationRule();
    }
  }

  get hasRules() {
    return Boolean(this.rules);
  }

  get hasLabel() {
    return Boolean(this.label);
  }

  get isRequired() {
    return this.hasRules && this.rules?.find(rule => rule.type === 'required');
  }

  get isTextField() {
    return this.type === 'text';
  }

  get isNumberField() {
    return this.type === 'number' || this.type === 'currency';
  }

  get isCheckboxField() {
    return this.type === 'checkbox';
  }

  get isTextareaField() {
    return this.type === 'textarea';
  }

  get isDateField() {
    return this.type === 'date';
  }

  get numericFormat() {
    if (isPlatformBrowser(this.platformId) && this.type === 'currency') {
      const format = this.textFieldConfig.getUseInputDecimals() ? '#0.00' : '#0';
      const symbol = this.currencySymbol || this.textFieldConfig.getCurrencySymbol();

      return `'${symbol}' ${format}`;
    }
    return undefined;
  }

  onValueChanged(value: string | number | boolean | Date | undefined) {
    if (value === '' && this.value === undefined) {
      return;
    }
    if (value !== this.value) {
      this.value = value;
      this.valueChange.emit(value);
    }
  }

  onFocusIn({ event }: any) {
    this.focusIn.emit(event);
  }

  onFocusOut({ event }: any) {
    if (typeof this.value === 'string') {
      this.onValueChanged(this.value.trim());
    }

    this.focusOut.emit(event);
  }

  onKeyPress($event: KeyboardEvent) {
    this.keyPress.emit($event);

    if (!this.inputAttrs.pattern) {
      return true;
    }
    if ($event.key.length > 1) {
      return true;
    }

    return new RegExp(this.inputAttrs.pattern, 'i').test($event.key);
  }

  onKeyDown($event: KeyboardEvent) {
    this.keyDown.emit($event);
  }

  onKeyUp($event: KeyboardEvent) {
    this.keyUp$.next($event);
    this.keyUp.emit($event);
  }

  onIsValidChanged(isValid: boolean) {
    setTimeout(() => {
      this.isValid = isValid;
    });
  }

  private getInputId(inputUid: number, inputAttrs?: InputAttr) {
    if (inputAttrs?.id) {
      return inputAttrs.id;
    }
    if (this.label) {
      return slugify(this.label).toLowerCase();
    }
    if (this.placeholder) {
      return slugify(this.placeholder).toLowerCase();
    }
    return `input-${inputUid}`;
  }

  private updateRequiredValidationRule() {
    const requiredRule = {
      type: 'required' as ValidationRuleType,
      message: this.textFieldConfig.getValidationRequiredText(),
    };

    this.rules = this.rules || [];
    const requiredRuleElement = this.rules.find(rule => rule.type === 'required');

    if (this.required) {
      if (!requiredRuleElement) {
        this.rules.unshift(requiredRule);
      }
    } else {
      if (requiredRuleElement) {
        this.rules = this.rules.filter(rule => rule.type !== 'required');
      }
    }
  }
}
